/** A calendar date without a time or timezone, serialized as YYYY-MM-DD. */
export type DateOnly = string;

export interface DateOnlyParts {
  year: number;
  month: number;
  day: number;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MILLISECONDS_PER_DAY = 86_400_000;

export const APP_TIME_ZONE = "Europe/Istanbul";

export function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

export function daysInMonth(year: number, month: number): number {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError("Geçersiz yıl veya ay.");
  }

  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;
  return lengths[month - 1] as number;
}

export function parseDateOnly(value: DateOnly): DateOnlyParts {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) {
    throw new RangeError("Tarih YYYY-MM-DD biçiminde olmalıdır.");
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError("Geçersiz takvim tarihi.");
  }

  return { year, month, day };
}

export function isDateOnly(value: string): value is DateOnly {
  try {
    parseDateOnly(value);
    return true;
  } catch {
    return false;
  }
}

export function makeDateOnly(year: number, month: number, day: number): DateOnly {
  if (!Number.isInteger(year) || year < 0 || year > 9999) {
    throw new RangeError("Yıl 0000 ile 9999 arasında olmalıdır.");
  }
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError("Geçersiz takvim tarihi.");
  }

  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Converts a date-only value to a timezone-independent integer day. */
export function dateOnlyToEpochDay(value: DateOnly): number {
  const { year, month, day } = parseDateOnly(value);
  return Math.floor(Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY);
}

export function epochDayToDateOnly(epochDay: number): DateOnly {
  if (!Number.isInteger(epochDay)) {
    throw new RangeError("Gün sıra numarası tam sayı olmalıdır.");
  }
  const date = new Date(epochDay * MILLISECONDS_PER_DAY);
  return makeDateOnly(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

export function addDateOnlyDays(value: DateOnly, amount: number): DateOnly {
  if (!Number.isInteger(amount)) {
    throw new RangeError("Eklenecek gün sayısı tam sayı olmalıdır.");
  }
  return epochDayToDateOnly(dateOnlyToEpochDay(value) + amount);
}

/** Concise alias for domain callers. */
export const addDays = addDateOnlyDays;

/** Returns end - start in calendar days. */
export function differenceInDateOnlyDays(end: DateOnly, start: DateOnly): number {
  return dateOnlyToEpochDay(end) - dateOnlyToEpochDay(start);
}

export function compareDateOnly(left: DateOnly, right: DateOnly): -1 | 0 | 1 {
  const comparison = differenceInDateOnlyDays(left, right);
  return comparison < 0 ? -1 : comparison > 0 ? 1 : 0;
}

export function isDateOnlyBetween(value: DateOnly, start: DateOnly, end: DateOnly): boolean {
  return compareDateOnly(value, start) >= 0 && compareDateOnly(value, end) <= 0;
}

export function enumerateDateOnlyRange(start: DateOnly, end: DateOnly): DateOnly[] {
  const count = differenceInDateOnlyDays(end, start);
  if (count < 0) {
    throw new RangeError("Bitiş tarihi başlangıç tarihinden önce olamaz.");
  }
  return Array.from({ length: count + 1 }, (_, index) => addDateOnlyDays(start, index));
}

export function positiveModulo(value: number, modulus: number): number {
  if (!Number.isInteger(modulus) || modulus <= 0) {
    throw new RangeError("Modül pozitif bir tam sayı olmalıdır.");
  }
  return ((value % modulus) + modulus) % modulus;
}

/** Returns true when a date-only value is Sunday, without timezone conversion. */
export function isSundayDateOnly(value: DateOnly): boolean {
  // Unix epoch day 0 (1970-01-01) was Thursday; Sunday is index 0.
  return positiveModulo(dateOnlyToEpochDay(value) + 4, 7) === 0;
}

/** Gets the local calendar date in Istanbul (or another explicit IANA zone). */
export function dateOnlyInTimeZone(value: Date, timeZone = APP_TIME_ZONE): DateOnly {
  if (Number.isNaN(value.getTime())) {
    throw new RangeError("Geçersiz tarih ve saat.");
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((item) => item.type === type)?.value;
    if (!found) throw new RangeError("Yerel tarih hesaplanamadı.");
    return Number(found);
  };
  return makeDateOnly(part("year"), part("month"), part("day"));
}

/** A Feb 29 anniversary is observed on Feb 28 in a non-leap year. */
export function anniversaryDateForYear(originalDate: DateOnly, year: number): DateOnly {
  const original = parseDateOnly(originalDate);
  const day = Math.min(original.day, daysInMonth(year, original.month));
  return makeDateOnly(year, original.month, day);
}
